import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createStyles, makeStyles } from '@mui/styles'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  CircularProgress,
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  TableSortLabel,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import QrCodeIcon from '@mui/icons-material/QrCode'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import RefreshIcon from '@mui/icons-material/Refresh'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import Env from '../../shared/config/envConfig'
import { setMessage } from '../../store/settings/settings.actions'
import { Severity } from '@3m5/crude-frontend/dist/shared/types/messageTypes'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 'calc(100% - 32px)',
      padding: theme.spacing(2),
      margin: theme.spacing(2),
      [theme.breakpoints.down('lg')]: {
        width: '100%',
        margin: 0,
        padding: theme.spacing(1),
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    qrThumbnail: {
      width: 40,
      height: 40,
      cursor: 'pointer',
      objectFit: 'contain',
      border: '1px solid ' + theme.palette.divider,
      borderRadius: 4,
    },
    filterContainer: {
      marginBottom: theme.spacing(3),
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    filterFormControl: {
      minWidth: 200,
      flexGrow: 1,
      [theme.breakpoints.down('lg')]: {
        width: '100%',
      },
    },
    qrDialogContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
      paddingTop: theme.spacing(1) + ' !important',
    },
    previewImage: {
      width: '100%',
      maxWidth: 400,
      height: 'auto',
      display: 'block',
      borderRadius: 8,
    },
    mobileCard: {
      marginBottom: theme.spacing(2),
      borderLeft: `6px solid ${theme.palette.primary.main}`,
    },
    mobileLabel: {
      fontWeight: 'bold',
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
    },
    mobileValue: {
      fontSize: '0.95rem',
    },
    statsContainer: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.spacing(1),
      display: 'grid',
      gridTemplateColumns: '140px repeat(3, 1fr)',
      gap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '100px repeat(3, 1fr)',
        fontSize: '0.8rem',
      },
    },
  }),
)

interface HelperPaymentDetailsDTO {
  paymentId: string | null
  helperId: string
  firstName: string
  lastName: string
  isClubMember: boolean
  dailyHours: Record<string, string>
  amount: number
  iban: string | null
  bic: string | null
  qrCodeImageId: string | null
  qrCodeUrl: string | null
  payed: boolean
  shirtNeeded: boolean
  comment: string | null
}

interface PaymentStatisticsDTO {
  totalAmount: number
  countTotal: number
  totalPayed: number
  countPayed: number
  totalUnpayed: number
  countUnpayed: number
  totalCashAmount: number
  countTotalCash: number
  payedCashAmount: number
  countPayedCash: number
  openCashAmount: number
  countOpenCash: number
  totalTransferAmount: number
  countTotalTransfer: number
  payedTransferAmount: number
  countPayedTransfer: number
  openTransferAmount: number
  countOpenTransfer: number
}

const AdminPayments: React.FC = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const [data, setData] = useState<HelperPaymentDetailsDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<PaymentStatisticsDTO | null>(null)
  const [toggling, setToggling] = useState<Record<string, boolean>>({})
  const [shirtLoading, setShirtLoading] = useState<Record<string, boolean>>({})
  const [qrCodes, setQrCodes] = useState<Record<string, string>>({})
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [memberFilter, setMemberFilter] = useState<'all' | 'member' | 'non-member'>('all')
  const [payedFilter, setPayedFilter] = useState<'all' | 'payed' | 'unpayed'>('all')
  const [orderBy, setOrderBy] = useState<keyof HelperPaymentDetailsDTO>('payed')
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editAmount, setEditAmount] = useState<string>('')
  const [updatingAmount, setUpdatingAmount] = useState<Record<string, boolean>>({})
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editComment, setEditComment] = useState<string>('')
  const [updatingComment, setUpdatingComment] = useState<Record<string, boolean>>({})
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false)
  const [senderName, setSenderName] = useState('')
  const [senderIban, setSenderIban] = useState('')
  const [senderBic, setSenderBic] = useState('')
  const [sepaLoading, setSepaLoading] = useState(false)

  const handleRequestSort = (property: keyof HelperPaymentDetailsDTO) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const fetchQrCode = useCallback(async (id: string): Promise<string | null> => {
    const restUrl = `${Env.basePath}/api/admin/payments/qr-code/${id}/base64`
    try {
      const response = await fetch(restUrl, { method: 'GET', credentials: 'include' })
      if (response.ok) {
        const base64 = await response.text()
        setQrCodes((prev) => ({ ...prev, [id]: base64 }))
        return base64
      }
    } catch (error) {
      console.error(`Fehler beim Laden des QR-Codes für ID ${id}:`, error)
    }
    return null
  }, [])

  const handleShowQrCode = useCallback(async (qrCodeImageId: string, cachedQrCodes: Record<string, string>) => {
    const imgData = cachedQrCodes[qrCodeImageId] ?? await fetchQrCode(qrCodeImageId)
    if (imgData) {
      setPreviewImage(imgData.startsWith('data:') ? imgData : `data:image/png;base64,${imgData}`)
      setDialogOpen(true)
    }
  }, [fetchQrCode])

  const fetchPriceStatistics = async () => {
    const restUrl = `${Env.basePath}/api/admin/payments/statistics`
    try {
      const response = await fetch(restUrl, {
        method: 'GET',
        credentials: 'include',
      })
      if (response.ok) {
        const result = await response.json()
        setStats(result)
      }
    } catch (error) {
      console.error('Fehler beim Laden der Preisstatistiken:', error)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    const restUrl = `${Env.basePath}/api/admin/payments/details`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Vergütungs-Übersicht')
      }
      const result = await response.json()
      setData(result)
    } catch (error) {
      console.error(error)
      dispatch(setMessage({
        severity: Severity.error,
        message: 'Die Vergütungs-Übersicht konnte nicht geladen werden.',
      }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    fetchPriceStatistics()
  }, [])

  const handleReload = () => {
    fetchData()
    fetchPriceStatistics()
  }

  const handleToggleShirt = async (helperId: string) => {
    setShirtLoading((prev) => ({ ...prev, [helperId]: true }))
    const restUrl = `${Env.basePath}/api/admin/plan/toggle-shirt?helperId=${helperId}`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })

      if (!response.ok) {
        throw new Error('Fehler beim Aktualisieren des Shirt-Status')
      }

      const newValue = await response.json()

      // Update local state for all records with this helperId
      setData((prevData) =>
        prevData.map((item) =>
          item.helperId === helperId ? { ...item, shirtNeeded: newValue } : item,
        ),
      )
      // Re-fetch data to ensure all calculations (e.g. amount) are updated
      fetchData()
      fetchPriceStatistics()
    } catch (error: any) {
      console.error(error)
      dispatch(setMessage({
        severity: Severity.error,
        message: 'Der Shirt-Status konnte nicht aktualisiert werden.',
      }))
    } finally {
      setShirtLoading((prev) => ({ ...prev, [helperId]: false }))
    }
  }

  const handleTogglePayed = async (paymentId: string) => {
    setToggling((prev) => ({ ...prev, [paymentId]: true }))
    const restUrl = `${Env.basePath}/api/admin/payments/toggle-payed?paymentId=${paymentId}`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Fehler beim Aktualisieren des Zahlungsstatus.')
      }

      const result = await response.json()
      if (result) {
        setData((prevData) =>
          prevData.map((item) =>
            item.paymentId === paymentId ? { ...item, payed: true } : item,
          ),
        )
        dispatch(setMessage({
          severity: Severity.success,
          message: 'Zahlungsstatus erfolgreich aktualisiert.',
        }))
      }
    } catch (error: any) {
      dispatch(setMessage({
        severity: Severity.error,
        message: error.message,
      }))
    } finally {
      setToggling((prev) => ({ ...prev, [paymentId]: false }))
    }
  }

  const handleUpdateAmount = async (paymentId: string | null, helperId: string) => {
    const amount = parseFloat(editAmount.replace(',', '.'))
    if (isNaN(amount)) {
      dispatch(setMessage({ severity: Severity.error, message: 'Bitte geben Sie einen gültigen Betrag ein.' }))
      return
    }

    setUpdatingAmount((prev) => ({ ...prev, [helperId]: true }))
    const params = paymentId ? `paymentId=${paymentId}&amount=${amount}` : `helperId=${helperId}&amount=${amount}`
    const restUrl = `${Env.basePath}/api/admin/payments/update-amount?${params}`

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      })
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Fehler beim Aktualisieren des Betrags.')
      }
      const updatedPayment: HelperPaymentDetailsDTO = await response.json()
      setData((prevData) =>
        prevData.map((item) => item.helperId === helperId ? { ...item, ...updatedPayment } : item),
      )
      setEditingId(null)
      dispatch(setMessage({ severity: Severity.success, message: 'Betrag erfolgreich aktualisiert.' }))
    } catch (error: any) {
      dispatch(setMessage({ severity: Severity.error, message: error.message }))
    } finally {
      setUpdatingAmount((prev) => ({ ...prev, [helperId]: false }))
    }
  }

  const handleUpdateComment = async (paymentId: string | null, helperId: string) => {
    setUpdatingComment((prev) => ({ ...prev, [helperId]: true }))
    const params = paymentId ? `paymentId=${paymentId}` : `helperId=${helperId}`
    const restUrl = `${Env.basePath}/api/admin/payments/update-comment?${params}`

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: editComment.trim() || null }),
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Fehler beim Speichern des Kommentars.')
      }

      const updatedPayment: HelperPaymentDetailsDTO = await response.json()
      setData((prevData) =>
        prevData.map((item) => item.helperId === helperId ? { ...item, ...updatedPayment } : item),
      )
      setEditingCommentId(null)
      dispatch(setMessage({ severity: Severity.success, message: 'Kommentar erfolgreich gespeichert.' }))
    } catch (error: any) {
      dispatch(setMessage({ severity: Severity.error, message: error.message }))
    } finally {
      setUpdatingComment((prev) => ({ ...prev, [helperId]: false }))
    }
  }

  const openTransferCount = useMemo(
    () => data.filter((item) => !item.payed && item.iban && item.amount > 0).length,
    [data],
  )

  const handleSepaExport = async () => {
    if (!senderName.trim() || !senderIban.trim() || !senderBic.trim()) {
      dispatch(setMessage({ severity: Severity.error, message: 'Bitte alle Absender-Felder ausfüllen.' }))
      return
    }
    setSepaLoading(true)
    const params = new URLSearchParams({
      senderName: senderName.trim(),
      senderIban: senderIban.trim(),
      senderBic: senderBic.trim(),
    })
    try {
      const response = await fetch(`${Env.basePath}/api/admin/payments/sepa-export?${params}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error('Export fehlgeschlagen')
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sepa-sammelueberweisung-${new Date().toISOString().slice(0, 10)}.xml`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setSepaDialogOpen(false)
      dispatch(setMessage({ severity: Severity.success, message: 'SEPA-Datei erfolgreich exportiert.' }))
    } catch (error: any) {
      dispatch(setMessage({ severity: Severity.error, message: 'Export fehlgeschlagen: ' + error.message }))
    } finally {
      setSepaLoading(false)
    }
  }

  const filteredData = useMemo(() => data.filter((item) => {
    const fullName = `${item.firstName} ${item.lastName}`.toLowerCase()
    const matchesSearch = fullName.includes(searchQuery.toLowerCase())
    const matchesMember =
      memberFilter === 'all' ||
      (memberFilter === 'member' && item.isClubMember) ||
      (memberFilter === 'non-member' && !item.isClubMember)
    const matchesPayed =
      payedFilter === 'all' ||
      (payedFilter === 'payed' && item.payed) ||
      (payedFilter === 'unpayed' && !item.payed)

    return matchesSearch && matchesMember && matchesPayed
  }).sort((a, b) => {
    const isAsc = order === 'asc'
    let comparison = 0

    if (orderBy === 'firstName' || orderBy === 'lastName') {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
      comparison = nameA.localeCompare(nameB)
    } else {
      const valA = a[orderBy]
      const valB = b[orderBy]

      if (valA === null || valA === undefined) comparison = 1
      else if (valB === null || valB === undefined) comparison = -1
      else if (valA < valB) comparison = -1
      else if (valA > valB) comparison = 1
    }

    return isAsc ? comparison : -comparison
  }), [data, searchQuery, memberFilter, payedFilter, orderBy, order])

  const allDays = useMemo(() => Array.from(
    new Set(data.flatMap((item) => Object.keys(item.dailyHours))),
  ).sort(), [data])

  return (
    <Paper className={classes.root}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' gutterBottom>
        <Typography variant='h5'>
          Helfer-Vergütung
        </Typography>
        <Stack direction='row' alignItems='center' gap={1}>
          <Button
            variant='outlined'
            startIcon={<AccountBalanceIcon />}
            onClick={() => setSepaDialogOpen(true)}
            disabled={loading}
            size='small'
          >
            Sammelüberweisung
          </Button>
          <IconButton onClick={handleReload} title='Daten neu laden' disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {!loading && (
        <Box className={classes.filterContainer}>
          <TextField
            label='Name suchen'
            variant='outlined'
            size='small'
            className={classes.filterFormControl}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <FormControl className={classes.filterFormControl} variant='outlined' size='small'>
            <InputLabel id='member-filter-label'>Vereinsmitglied</InputLabel>
            <Select
              labelId='member-filter-label'
              value={memberFilter}
              onChange={(e) => setMemberFilter(e.target.value as any)}
              label='Vereinsmitglied'
            >
              <MenuItem value='all'>Alle</MenuItem>
              <MenuItem value='member'>Ja (Mitglied)</MenuItem>
              <MenuItem value='non-member'>Nein (Kein Mitglied)</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterFormControl} variant='outlined' size='small'>
            <InputLabel id='payed-filter-label'>Bezahlt</InputLabel>
            <Select
              labelId='payed-filter-label'
              value={payedFilter}
              onChange={(e) => setPayedFilter(e.target.value as any)}
              label='Bezahlt'
            >
              <MenuItem value='all'>Alle</MenuItem>
              <MenuItem value='payed'>Ja (bezahlt)</MenuItem>
              <MenuItem value='unpayed'>Nein (offen)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {stats && (
        <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>Statistiken</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box className={classes.statsContainer}>
              {/* Header row */}
              <Box />
              {(['Gesamt', 'Überweisung', 'Bar'] as const).map((col) => (
                <Typography key={col} variant='subtitle2' sx={{ fontWeight: 'bold', textAlign: 'center' }}>{col}</Typography>
              ))}
              {/* Row: Gesamtkosten */}
              <Typography variant='body2' color='textSecondary' sx={{ alignSelf: 'center' }}>Gesamtkosten</Typography>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ fontWeight: 'bold' }}>{stats.totalAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countTotal} Helfer)</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ fontWeight: 'bold' }}>{stats.totalTransferAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countTotalTransfer} Helfer)</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ fontWeight: 'bold' }}>{stats.totalCashAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countTotalCash} Helfer)</Typography></Box>
              {/* Row: davon ausgezahlt */}
              <Typography variant='body2' color='textSecondary' sx={{ alignSelf: 'center' }}>davon ausgezahlt</Typography>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ color: 'success.main' }}>{stats.totalPayed.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countPayed} Helfer)</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ color: 'success.main' }}>{stats.payedTransferAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countPayedTransfer} Helfer)</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ color: 'success.main' }}>{stats.payedCashAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countPayedCash} Helfer)</Typography></Box>
              {/* Row: davon offen */}
              <Typography variant='body2' color='textSecondary' sx={{ alignSelf: 'center' }}>davon offen</Typography>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ color: 'primary.main' }}>{stats.totalUnpayed.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countUnpayed} Helfer)</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ color: 'primary.main' }}>{stats.openTransferAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countOpenTransfer} Helfer)</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant='body1' sx={{ color: 'primary.main' }}>{stats.openCashAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Typography><Typography variant='caption' color='textSecondary'>({stats.countOpenCash} Helfer)</Typography></Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box>
          {filteredData.map((row) => (
            <Card key={row.helperId} className={classes.mobileCard}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant='h6' color='primary'>{row.firstName} {row.lastName}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography className={classes.mobileLabel}>Vereinsmitglied</Typography>
                    <Typography className={classes.mobileValue}>
                      {row.isClubMember ? <CheckIcon color='success' fontSize='small' /> : '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography className={classes.mobileLabel}>Shirt erhalten</Typography>
                    <Checkbox
                      checked={row.shirtNeeded}
                      onChange={() => handleToggleShirt(row.helperId)}
                      disabled={shirtLoading[row.helperId]}
                      size='small'
                      sx={{ p: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {allDays.map((day) => (
                    <Grid item xs={4} key={`${row.helperId}-${day}`}>
                      <Typography className={classes.mobileLabel}>{day}</Typography>
                      <Typography className={classes.mobileValue}>{row.dailyHours[day] || '0'} h</Typography>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography className={classes.mobileLabel}>Betrag</Typography>
                    {editingId === row.helperId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <TextField
                          size='small'
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          autoFocus
                          sx={{ width: 80 }}
                        />
                        <IconButton
                          size='small'
                          onClick={() => handleUpdateAmount(row.paymentId, row.helperId)}
                          disabled={updatingAmount[row.helperId]}
                        >
                          {updatingAmount[row.helperId] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon fontSize='small' color='primary' />
                          )}
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography className={classes.mobileValue} sx={{ fontWeight: 'bold' }}>
                          {row.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setEditingId(row.helperId)
                            setEditAmount(row.amount.toString().replace('.', ','))
                          }}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    <Typography className={classes.mobileLabel}>Ausgezahlt</Typography>
                    {row.payed ? (
                      <CheckIcon color='success' fontSize='small' />
                    ) : (
                      <Checkbox
                        checked={row.payed}
                        onChange={() => row.paymentId && handleTogglePayed(row.paymentId)}
                        disabled={!row.paymentId || toggling[row.paymentId]}
                        color='primary'
                        size='small'
                        sx={{ p: 0 }}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.mobileLabel}>IBAN / BIC</Typography>
                    <Typography className={classes.mobileValue}>{row.iban || '-'} / {row.bic || '-'}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.mobileLabel}>Kommentar</Typography>
                    {editingCommentId === row.helperId ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                        <TextField
                          size='small'
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          autoFocus
                          multiline
                          maxRows={4}
                          fullWidth
                        />
                        <IconButton
                          size='small'
                          onClick={() => handleUpdateComment(row.paymentId, row.helperId)}
                          disabled={updatingComment[row.helperId]}
                        >
                          {updatingComment[row.helperId] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon fontSize='small' color='primary' />
                          )}
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography className={classes.mobileValue} sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                          {row.comment || '-'}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setEditingCommentId(row.helperId)
                            setEditComment(row.comment || '')
                          }}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {row.qrCodeImageId && (
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <Button
                        variant='outlined'
                        startIcon={<QrCodeIcon />}
                        size='small'
                        onClick={() => handleShowQrCode(row.qrCodeImageId!, qrCodes)}
                      >
                        QR Code anzeigen
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          ))}
          {filteredData.length === 0 && (
            <Typography align='center' sx={{ py: 4 }}>
              Keine Vergütungsdaten gefunden.
            </Typography>
          )}
        </Box>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader aria-label='admin payments table'>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'lastName' || orderBy === 'firstName'}
                    direction={orderBy === 'lastName' || orderBy === 'firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('lastName')}
                  >
                    <b>Name</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center'>
                  <TableSortLabel
                    active={orderBy === 'isClubMember'}
                    direction={orderBy === 'isClubMember' ? order : 'asc'}
                    onClick={() => handleRequestSort('isClubMember')}
                  >
                    <b>Mitglied</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align='center'>
                  <TableSortLabel
                    active={orderBy === 'shirtNeeded'}
                    direction={orderBy === 'shirtNeeded' ? order : 'asc'}
                    onClick={() => handleRequestSort('shirtNeeded')}
                  >
                    <b>Shirt erhalten</b>
                  </TableSortLabel>
                </TableCell>
                {allDays.map((day) => (
                  <TableCell key={day}><b>{day} (Std)</b></TableCell>
                ))}
                <TableCell><b>Betrag</b></TableCell>
                <TableCell><b>IBAN</b></TableCell>
                <TableCell><b>BIC</b></TableCell>
                <TableCell align='center'><b>QR</b></TableCell>
                <TableCell align='center'>
                  <TableSortLabel
                    active={orderBy === 'payed'}
                    direction={orderBy === 'payed' ? order : 'asc'}
                    onClick={() => handleRequestSort('payed')}
                  >
                    <b>Ausgezahlt</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell><b>Kommentar</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row) => (
                <TableRow key={row.helperId}>
                  <TableCell>{row.firstName} {row.lastName}</TableCell>
                  <TableCell align='center'>
                    {row.isClubMember ? <CheckIcon color='success' /> : '-'}
                  </TableCell>
                  <TableCell align='center'>
                    <Checkbox
                      checked={row.shirtNeeded}
                      onChange={() => handleToggleShirt(row.helperId)}
                      disabled={shirtLoading[row.helperId]}
                      size='small'
                    />
                  </TableCell>
                  {allDays.map((day) => (
                    <TableCell key={`${row.helperId}-${day}`}>
                      {row.dailyHours[day] || '-'}
                    </TableCell>
                  ))}
                  <TableCell>
                    {editingId === row.helperId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size='small'
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          autoFocus
                          sx={{ width: 80 }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateAmount(row.paymentId, row.helperId)
                            }
                          }}
                        />
                        <IconButton
                          size='small'
                          onClick={() => handleUpdateAmount(row.paymentId, row.helperId)}
                          disabled={updatingAmount[row.helperId]}
                        >
                          {updatingAmount[row.helperId] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon fontSize='small' color='primary' />
                          )}
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                        <IconButton
                          size='small'
                          onClick={() => {
                            setEditingId(row.helperId)
                            setEditAmount(row.amount.toString().replace('.', ','))
                          }}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{row.iban || '-'}</TableCell>
                  <TableCell>{row.bic || '-'}</TableCell>
                  <TableCell align='center'>
                    {row.qrCodeImageId ? (
                      <IconButton size='small' onClick={() => handleShowQrCode(row.qrCodeImageId!, qrCodes)}>
                        <QrCodeIcon fontSize='small' />
                      </IconButton>
                    ) : '-'}
                  </TableCell>
                  <TableCell align='center'>
                    {row.payed ? (
                      <CheckIcon color='success' />
                    ) : (
                      <Checkbox
                        checked={row.payed}
                        onChange={() => row.paymentId && handleTogglePayed(row.paymentId)}
                        disabled={!row.paymentId || toggling[row.paymentId]}
                        color='primary'
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCommentId === row.helperId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size='small'
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          autoFocus
                          multiline
                          maxRows={3}
                          sx={{ minWidth: 160 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              handleUpdateComment(row.paymentId, row.helperId)
                            }
                          }}
                        />
                        <IconButton
                          size='small'
                          onClick={() => handleUpdateComment(row.paymentId, row.helperId)}
                          disabled={updatingComment[row.helperId]}
                        >
                          {updatingComment[row.helperId] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon fontSize='small' color='primary' />
                          )}
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', maxWidth: 200 }}>
                          {row.comment || '-'}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setEditingCommentId(row.helperId)
                            setEditComment(row.comment || '')
                          }}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10 + allDays.length} align='center'>
                    Keine Vergütungsdaten gefunden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle align='center'>Überweisung mit QR-Code</DialogTitle>
        <DialogContent className={classes.qrDialogContent}>
          {previewImage && (
            <img src={previewImage} alt='QR Preview' className={classes.previewImage} />
          )}
          <Button onClick={() => setDialogOpen(false)} variant='outlined' fullWidth sx={{ mt: 1 }}>
            Schließen
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={sepaDialogOpen}
        onClose={() => !sepaLoading && setSepaDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>SEPA Sammelüberweisung exportieren</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
            Exportiert alle offenen Überweisungen ({openTransferCount} Helfer) als SEPA pain.001 XML-Datei.
            Diese Datei kann direkt im Online-Banking hochgeladen werden.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Absender Name'
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              fullWidth
              required
              disabled={sepaLoading}
              placeholder='z.B. Schlosstriathlon e.V.'
            />
            <TextField
              label='Absender IBAN'
              value={senderIban}
              onChange={(e) => setSenderIban(e.target.value)}
              fullWidth
              required
              disabled={sepaLoading}
              placeholder='DE89 3704 0044 0532 0130 00'
            />
            <TextField
              label='Absender BIC'
              value={senderBic}
              onChange={(e) => setSenderBic(e.target.value)}
              fullWidth
              required
              disabled={sepaLoading}
              placeholder='COBADEFFXXX'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSepaDialogOpen(false)} disabled={sepaLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSepaExport}
            variant='contained'
            disabled={sepaLoading || !senderName.trim() || !senderIban.trim() || !senderBic.trim()}
            startIcon={sepaLoading ? <CircularProgress size={18} /> : <AccountBalanceIcon />}
          >
            {sepaLoading ? 'Exportiere...' : 'Export starten'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default AdminPayments
